import React, { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { CardContainer } from "../../../components/Card/CardContainer";
import {
  Col,
  Row,
  Button as AntButton,
  Divider,
  DatePicker,
  Card,
  Popover,
  Typography,
  Tag,
  Progress,
  Table,
  Spin,
  Tabs,
  message,
} from "antd";
import Button from "../../../components/Button/Button";
import {
  BarChartOutlined,
  ExclamationCircleOutlined,
  UnorderedListOutlined,
  VerticalAlignBottomOutlined,
} from "@ant-design/icons";
import Select from "../../../components/Select/Select";
import Text from "../../../components/Text/Text";
import { color, icon } from "../../../resource";
import dayjs from "dayjs";
import { numberFormatter } from "../../../utility/Formatter";
import {
  excelMutiCoDashboard,
  getCoDiscountDashboard,
} from "../../../datasource/CODiscountDatasource";
import { FlexCol } from "../../../components/Container/Container";
import Permission, { checkPermission } from "../../../components/Permission/Permission";
import { ActionBtn } from "../../../components/Button/TableActionButton";
import { useRecoilValue } from "recoil";
import { roleAtom } from "../../../store/RoleAtom";
import { zoneDatasource } from "../../../datasource/ZoneDatasource";
import { useEffectOnce } from "react-use";
import { useNotificationContext } from "../../../context/NotificationProvider";

export const IndexCODiscountDashboard: React.FC = () => {
  const profile = JSON.parse(localStorage.getItem("profile")!);
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const roleData = useRecoilValue(roleAtom);
  const isViewCoType = checkPermission(["coDiscountType", "view"], roleData);
  const isViewCoList = checkPermission(["coDiscountList", "view"], roleData);
  const isExport = checkPermission(["coDiscountDashboard", "excel"], roleData);

  const [data, setData] = useState<any>(null);
  const [searchStatus, setSearchStatus] = useState<string>("ACTIVE");
  const [searchDate, setSearchDate] = useState<string>("year");
  const [selectedTeam, setSelectedTeam] = useState<string[]>([]);
  const [selectedZone, setSelectedZone] = useState<string[]>([]);

  const [teamList, setTeamList] = useState<any[]>([]);
  const [zoneList, setZoneList] = useState<any[]>([]);
  const [loading, setLoading] = useState<boolean>(false);

  const today = dayjs();
  const quarter = Math.floor(today.month() / 3);
  const quarterStartMonth = quarter * 3;

  const [isExporting, setIsExporting] = useState(false);
  const { startUploadProcess } = useNotificationContext();
  const [progressValue, setProgressValue] = useState(0);

  const searchDateOptions = [
    {
      key: "all",
      value: "3 ปี ย้อนหลัง",
      startDate: today.subtract(3, "year").startOf("day"),
      endDate: today.endOf("day"),
      isDisabled: true,
    },
    {
      key: "month",
      value: "เดือนนี้",
      startDate: today.startOf("month"),
      endDate: today.endOf("month"),
      isDisabled: true,
    },
    {
      key: "quarter",
      value: "ไตรมาสนี้",
      startDate: today.month(quarterStartMonth).startOf("month"),
      endDate: today.month(quarterStartMonth + 2).endOf("month"),
      isDisabled: true,
    },
    {
      key: "year",
      value: "ปีนี้",
      startDate: today.startOf("year"),
      endDate: today.endOf("year"),
      isDisabled: true,
    },
    {
      key: "custom",
      value: "กำหนดเอง",
      startDate: undefined,
      endDate: undefined,
      isDisabled: false,
    },
  ];
  const searchStatusOptions = [
    { key: "ACTIVE", value: "เปิดใช้งาน", color: color.EmeraldGreen },
    { key: "EXPIRED", value: "หมดอายุ", color: color.error },
    { key: "INACTIVE", value: "ปิดใช้งาน", color: color.Grey },
  ];
  const [period, setPeriod] = useState<[any, any]>(
    searchDateOptions.find((option) => option.key === "year")
      ? [
          searchDateOptions.find((option) => option.key === "year")!.startDate,
          searchDateOptions.find((option) => option.key === "year")!.endDate,
        ]
      : [undefined, undefined],
  );

  const fetchData = async () => {
    await getCoDiscountDashboard({
      company: profile.company,
      status: [searchStatus],
      startDate: period[0] && period[0].format("YYYY-MM-DD"),
      endDate: period[1] && period[1].format("YYYY-MM-DD"),
      zone: selectedZone.length > 0 ? selectedZone.join(",") : undefined,
    }).then((res) => {
      setData(res);
    });
  };

  useEffect(() => {
    setLoading(true);
    fetchData();
    setTimeout(() => {
      setLoading(false);
    }, 1000);
  }, [searchStatus, period, selectedZone]);

  const fetchZone = async () => {
    const res = await zoneDatasource.getAllZoneByCompany(profile.company);
    const mapGroup = Object.values(
      res.reduce((acc: any, item: any) => {
        const teamId = item.team.teamId;
        if (!acc[teamId]) {
          acc[teamId] = {
            teamId,
            teamName: item.team.teamName,
            zones: [],
          };
        }
        acc[teamId].zones.push(item);
        return acc;
      }, {}),
    );
    setTeamList(mapGroup);
    setZoneList(res);
  };

  useEffectOnce(() => {
    fetchZone();
  });

  const teamOptions = React.useMemo(
    () =>
      teamList.map((team) => ({
        key: team.teamId,
        value: team.teamId,
        label: team.teamName,
        zones: team.zones,
      })),
    [teamList],
  );
  const zoneFilter = React.useMemo(() => {
    if (!selectedTeam.length) {
      return zoneList;
    }
    return zoneList.map((zone: any) => {
      return { ...zone, checked: selectedTeam.includes(zone.team?.teamId) };
    });
  }, [zoneList, selectedTeam]);

  const SearchSection = () => {
    return (
      <Row gutter={8} justify={"end"} align={"middle"}>
        <Col style={{ display: "flex", alignItems: "center" }}>
          <Text>สถานะ</Text>
        </Col>
        {searchStatusOptions.map((item, index) => (
          <Col key={index}>
            <AntButton
              shape='round'
              style={{
                backgroundColor: searchStatus === item.key ? `${item.color}20` : undefined,
                color: searchStatus === item.key ? item.color : undefined,
                borderColor: searchStatus === item.key ? item.color : color.background2,
              }}
              onClick={() => {
                setSearchStatus(item.key);
              }}
            >
              <Text
                fontWeight={600}
                style={{ color: searchStatus === item.key ? item.color : color.Disable }}
              >
                {item.value}
              </Text>
            </AntButton>
          </Col>
        ))}
        <Col>
          <Divider
            type='vertical'
            style={{ height: 40, borderColor: color.background2 }}
            size='small'
          />
        </Col>
        {searchDateOptions.map((item, index) => (
          <Col key={index}>
            <AntButton
              shape='round'
              style={{
                backgroundColor: searchDate === item.key ? color.primary : undefined,
                color: searchDate === item.key ? color.white : undefined,
                borderColor: searchDate === item.key ? color.primary : undefined,
              }}
              onClick={() => {
                setSearchDate(item.key);
                item.startDate && item.endDate && setPeriod([item.startDate, item.endDate]);
              }}
            >
              <Text
                fontWeight={600}
                style={{ color: searchDate === item.key ? color.white : color.BK }}
              >
                {item.value}
              </Text>
            </AntButton>
          </Col>
        ))}
        <Col>
          <DatePicker.RangePicker
            format='DD/MM/YYYY'
            allowClear={false}
            allowEmpty={[true, true]}
            disabled={
              searchDateOptions.find((option) => option.isDisabled && searchDate === option.key)
                ? true
                : false
            }
            // disabledDate={(current: any) => {
            //   return current && current.isAfter(dayjs().endOf("day"));
            // }}
            style={{ width: "100%", color: color.BK }}
            value={[period[0] ? dayjs(period[0]) : null, period[1] ? dayjs(period[1]) : null]}
            onChange={(dates) => {
              if (dates && dates[0] && dates[1]) {
                setPeriod([dates[0], dates[1]]);
              } else {
                setPeriod([undefined, undefined]);
              }
            }}
          />
        </Col>
      </Row>
    );
  };
  const DashboardSection = () => {
    const mapData = [
      {
        type: "ส่วนลดรวม",
        value: `${numberFormatter(data?.amountSummary?.totalDiscount || 0)} ฿`,
        color: "#0068F4",
        bgColor: "#EEF6FE",
      },
      {
        type: "ใช้ไป",
        value: `${numberFormatter(data?.amountSummary?.used || 0)} ฿`,
        color: "#F24040",
        bgColor: "#FEECEC",
      },
      {
        type: "คงเหลือ",
        value: `${numberFormatter(data?.amountSummary?.remaining || 0)} ฿`,
        color: "#008236",
        bgColor: "#F0FDF4",
      },
      {
        type: "ใกล้หมดอายุ (รายการ)",
        value: data?.amountSummary?.nearExpiryCount || 0,
        color: "#C1A100",
        bgColor: "#FFF7C9",
      },
    ];
    const mapSubData = [
      { type: "รายการทั้งหมด", value: data?.countSummary?.all || 0, bgColor: "#EEF6FE" },
      // { type: "เปิดใช้งาน", value: data?.countSummary?.active || 0, bgColor: "#EEF6FE" },
      { type: "ร้านที่มี CO", value: data?.countSummary?.shopCount || 0, bgColor: "#EEF6FE" },
      {
        type: "ประเภท CO",
        value: data?.countSummary?.creditMemoTypeCount || 0,
        bgColor: "#EEF6FE",
      },
    ];

    return (
      <>
        <Row justify={"space-between"} gutter={8}>
          {mapData.map((item, index) => (
            <Col span={6} key={index}>
              <Card
                style={{
                  backgroundColor: item.bgColor,
                  borderColor: item.bgColor,
                  borderRadius: 5,
                }}
              >
                <span
                  className='card-label font-weight-bolder'
                  style={{
                    fontSize: 30,
                    fontWeight: "bold",
                    color: item.color,
                    borderRadius: 5,
                  }}
                >
                  {item.value}
                </span>
                <br />
                <Text style={{ fontSize: 16 }}>{item.type}</Text>{" "}
                {/* {index == 0 && (
                  <Popover
                    placement='bottom'
                    content={
                      <Text level={6} color='white'>
                        เฉพาะร้านที่เปิดใช้งาน
                      </Text>
                    }
                    color={color.BK}
                  >
                    <ExclamationCircleOutlined />
                  </Popover>
                )} */}
              </Card>
            </Col>
          ))}
        </Row>
        <br />
        <Row justify={"space-between"} gutter={8}>
          {mapSubData.map((item, index) => (
            <Col span={24 / mapSubData.length} key={index}>
              <Card
                bodyStyle={{ paddingRight: 20, paddingLeft: 20, paddingTop: 5, paddingBottom: 5 }}
              >
                <span
                  style={{
                    fontSize: 25,
                    fontWeight: "bold",
                    borderRadius: 5,
                  }}
                >
                  {numberFormatter(item.value, 0)}
                </span>
                <br />
                <Row gutter={8}>
                  <Col>
                    <Text level={6} style={{ fontSize: 16 }} color='Text3'>
                      {item.type}
                    </Text>
                  </Col>
                  {index === 0 && (
                    <Col>
                      {(() => {
                        const find = searchStatusOptions.find(
                          (option) => option.key === searchStatus,
                        );
                        return (
                          <>
                            <Tag
                              style={{
                                backgroundColor: `${find?.color}20`,
                                borderRadius: 5,
                                border: "none",
                              }}
                            >
                              <Text level={6} style={{ color: find?.color }} fontWeight={600}>
                                {find?.value}
                              </Text>
                            </Tag>
                          </>
                        );
                      })()}
                    </Col>
                  )}
                </Row>
              </Card>
            </Col>
          ))}
        </Row>
      </>
    );
  };
  const CoListProgressSection = () => {
    const [tabKey, setTabKey] = useState("ALL");
    const itemTab = [
      {
        key: "ALL",
        label: (
          <Text level={6} color={tabKey === "ALL" ? "primary" : "Text1"}>
            ทั้งหมด ({data?.activeCos?.data?.length ?? 0})
          </Text>
        ),
      },
      {
        key: "REMAINING",
        label: (
          <Text level={6} color={tabKey === "REMAINING" ? "primary" : "Text1"}>
            คงเหลือ ({data?.activeCos?.data?.filter((f) => f?.group === "REMAINING")?.length ?? 0})
          </Text>
        ),
      },
      ...(searchStatus === "ACTIVE"
        ? [
            {
              key: "NEAR_EXPIRY",
              label: (
                <Text level={6} color={tabKey === "NEAR_EXPIRY" ? "primary" : "Text1"}>
                  ใกล้หมดอายุ (
                  {data?.activeCos?.data?.filter((f) => f?.group === "NEAR_EXPIRY")?.length ?? 0})
                </Text>
              ),
            },
          ]
        : []),
      {
        key: "USED_UP",
        label: (
          <Text level={6} color={tabKey === "USED_UP" ? "primary" : "Text1"}>
            ใช้หมด ({data?.activeCos?.data?.filter((f) => f?.group === "USED_UP")?.length ?? 0})
          </Text>
        ),
      },
    ];
    const mapData = data?.activeCos.data.filter((f) => {
      if (tabKey !== "ALL") {
        return f?.group === tabKey;
      }
      return true;
    });

    return (
      <Card>
        <div style={{ paddingBottom: 10 }}>
          <Text fontWeight={600}>
            CO{" "}
            {searchStatus === "ACTIVE"
              ? "ที่กำลังเปิดใช้ตอนนี้"
              : searchStatus === "EXPIRED"
                ? "ที่หมดอายุแล้ว"
                : "ที่ปิดใช้งานแล้ว"}
          </Text>
        </div>
        <Tabs activeKey={tabKey} onChange={(key) => setTabKey(key)} items={itemTab} />
        {mapData && mapData?.length > 0 ? (
          <div style={{ minHeight: 420, maxHeight: 420, overflowY: "auto", paddingRight: 8 }}>
            {mapData.map((item: any, index: number) => (
              <>
                <Row key={index} justify={"space-between"} align='middle' gutter={8}>
                  <Col span={isViewCoList ? 23 : 24}>
                    <React.Fragment>
                      <div style={{ paddingBottom: 10 }}>
                        <Row justify={"space-between"} align='middle' gutter={8}>
                          <Col>
                            <Row justify={"start"} gutter={8}>
                              <Col>
                                <Tag
                                  style={{
                                    backgroundColor: `${item?.creditMemoType?.color}20`,
                                    border: "none",
                                  }}
                                >
                                  <Typography.Text
                                    style={{
                                      fontFamily: "IBM Plex Sans Thai",
                                      color: item?.creditMemoType?.color,
                                      fontWeight: "bold",
                                      fontSize: 15,
                                    }}
                                  >
                                    {item?.creditMemoType.code}
                                  </Typography.Text>
                                </Tag>
                              </Col>
                              <Col>
                                <Text fontWeight={600}>{item?.name}</Text>
                              </Col>
                              <Col>
                                <Text color='Text3' level={6}>
                                  (<img src={icon.shop_co} style={{ height: "16px" }} />{" "}
                                  {item?.shopCount})
                                </Text>
                              </Col>
                            </Row>
                          </Col>
                          <Col>
                            <Text color='EmeraldGreen' fontWeight={600}>
                              คงเหลือ {numberFormatter(item.remaining)} ฿
                            </Text>
                            {" / "}
                            <Text color='Text3' level={6}>
                              {numberFormatter(item.totalDiscount)} ฿
                            </Text>
                          </Col>
                        </Row>
                        <Progress
                          key={index}
                          strokeColor={item.creditMemoType?.color}
                          percent={item.totalDiscount ? (item.used / item.totalDiscount) * 100 : 0}
                          showInfo={false}
                        />
                        <Row justify={"space-between"} gutter={8}>
                          <Col>
                            <Text level={6} fontWeight={600}>
                              ใช้ไป {numberFormatter(item.used)} ฿ (
                              {item.totalDiscount
                                ? ((item?.used / item?.totalDiscount) * 100).toFixed(2)
                                : 0}
                              %)
                            </Text>
                          </Col>
                          <Col>
                            <Text level={6} color='Grey'>
                              {dayjs(item?.startDate).format("DD/MM/YYYY")} -{" "}
                              {dayjs(item?.endDate).format("DD/MM/YYYY")} •{" "}
                              {searchStatus === "ACTIVE" &&
                                `เหลืออีก ${numberFormatter(item.daysLeft, 0)} วัน`}
                              {searchStatus === "EXPIRED" &&
                                `หมดอายุแล้ว ${numberFormatter(Math.abs(item.daysLeft * -1), 0)} วัน`}
                              {searchStatus === "INACTIVE" && `ปิดใช้งานแล้ว`}
                            </Text>
                          </Col>
                        </Row>
                      </div>
                    </React.Fragment>
                  </Col>
                  <Col>
                    <Permission permission={["coDiscountList", "view"]}>
                      <ActionBtn
                        onClick={() =>
                          navigate(
                            "/newDiscountCo/detailCODiscountList/" + item.coId + "/coDashboard",
                          )
                        }
                        icon={<UnorderedListOutlined />}
                      />
                    </Permission>
                  </Col>
                </Row>
                <Divider size='small' />
              </>
            ))}
          </div>
        ) : (
          <FlexCol style={{ height: "200px", alignItems: "center", justifyContent: "center" }}>
            <BarChartOutlined style={{ fontSize: 50, color: color.Text3 }} />
            <Text color='Text3' level={4}>
              ไม่มีข้อมูล
            </Text>
          </FlexCol>
        )}
      </Card>
    );
  };
  const CoZoneProgressSection = () => {
    const column = [
      {
        title: <Text fontWeight={600}>เขต</Text>,
        dataIndex: "zone",
        key: "zone",
        render: (value: any, row: any, index: number) => {
          return {
            children: (
              <Text level={6} fontWeight={600}>
                {value}
              </Text>
            ),
          };
        },
      },
      {
        title: <Text fontWeight={600}>รายการ CO</Text>,
        dataIndex: "coCount",
        key: "coCount",
        render: (value: any, row: any, index: number) => {
          const mapData =
            searchStatus === "ACTIVE"
              ? row.activeCount
              : searchStatus === "EXPIRED"
                ? row.expiredCount
                : row.inactiveCount;
          return {
            children: (
              <FlexCol align={"end"} style={{ cursor: "default" }}>
                <Text level={6} fontWeight={600}>
                  {numberFormatter(mapData, 0)}
                </Text>
              </FlexCol>
            ),
          };
        },
      },
      {
        title: <Text fontWeight={600}>ใช้ไป (บาท)</Text>,
        dataIndex: "used",
        key: "used",
        render: (value: any, row: any, index: number) => {
          return {
            children: (
              <FlexCol align={"end"}>
                <Text level={6} fontWeight={600}>
                  {numberFormatter(value)} ฿
                </Text>
              </FlexCol>
            ),
          };
        },
      },
      {
        title: <Text fontWeight={600}>คงเหลือ (บาท)</Text>,
        dataIndex: "remaining",
        key: "remaining",
        render: (value: any, row: any, index: number) => {
          return {
            children: (
              <FlexCol align={"end"}>
                <Text level={6} fontWeight={600} color='EmeraldGreen'>
                  {numberFormatter(value)} ฿
                </Text>
              </FlexCol>
            ),
          };
        },
      },
    ];

    return (
      <Card bodyStyle={{ height: "470px" }}>
        <Text fontWeight={600}>
          CO ตามเขต
          {searchStatus === "ACTIVE"
            ? "ที่กำลังเปิดใช้ตอนนี้"
            : searchStatus === "EXPIRED"
              ? "ที่หมดอายุแล้ว"
              : "ที่ปิดใช้งานแล้ว"}
        </Text>
        <br />
        <Table
          columns={column}
          dataSource={data?.byZone || []}
          pagination={false}
          rowClassName={(record) => (record.zoneCode === "C01" ? "zone-c01-row" : "")}
          scroll={{ y: 300 }}
          locale={{
            emptyText: (
              <FlexCol style={{ height: "350px", alignItems: "center", justifyContent: "center" }}>
                <BarChartOutlined style={{ fontSize: 50, color: color.Text3 }} />
                <Text color='Text3'>ไม่มีข้อมูล</Text>
              </FlexCol>
            ),
          }}
        />
      </Card>
    );
  };
  const CoTypeProgressSection = () => {
    return (
      <Card bodyStyle={{ height: "470px" }}>
        <div style={{ paddingBottom: 4 }}>
          <Text fontWeight={600}>5 อันดับยอดใช้ส่วนลดสูงสุดตามประเภท</Text>
        </div>
        <br />
        {data?.topCreditMemoTypes && data?.topCreditMemoTypes?.length > 0 ? (
          <>
            {data?.topCreditMemoTypes.map((item: any, index: number) => (
              <React.Fragment key={index}>
                <div style={{ paddingBottom: 10 }}>
                  <Row justify={"space-between"} align='middle' gutter={8}>
                    <Col
                      span={8}
                      onClick={() =>
                        isViewCoType &&
                        navigate(
                          "/newDiscountCo/detailDiscountType/" +
                            item.creditMemoTypeId +
                            "/coDashboard",
                        )
                      }
                      style={{ cursor: isViewCoType ? "pointer" : "default" }}
                    >
                      <Row justify={"start"} gutter={8}>
                        <Col>
                          <Tag
                            style={{
                              backgroundColor: `${item?.color}20`,
                              border: "none",
                            }}
                          >
                            <Typography.Text
                              style={{
                                fontFamily: "IBM Plex Sans Thai",
                                color: item?.color,
                                fontWeight: "bold",
                                fontSize: 15,
                              }}
                            >
                              {item.code} • {item.nameTh}
                            </Typography.Text>
                          </Tag>
                        </Col>
                      </Row>
                    </Col>
                    <Col>
                      <Text color='EmeraldGreen' fontWeight={600}>
                        {numberFormatter(item.remaining)} ฿
                      </Text>
                      {" / "}
                      <Text color='Text3' level={6}>
                        {numberFormatter(item.totalDiscount)} ฿
                      </Text>{" "}
                    </Col>
                  </Row>
                  <Progress
                    key={index}
                    strokeColor={item?.color}
                    percent={item.totalDiscount ? (item.used / item.totalDiscount) * 100 : 0}
                    showInfo={false}
                  />
                  <Row justify={"start"} gutter={8}>
                    <Col>
                      <Text color='Grey' level={6} fontWeight={600}>
                        ใช้ไป {numberFormatter(item.used)} ฿
                      </Text>
                    </Col>
                  </Row>
                </div>
              </React.Fragment>
            ))}
          </>
        ) : (
          <FlexCol style={{ height: "350px", alignItems: "center", justifyContent: "center" }}>
            <BarChartOutlined style={{ fontSize: 50, color: color.Text3 }} />
            <Text color='Text3'>ไม่มีข้อมูล</Text>
          </FlexCol>
        )}
      </Card>
    );
  };
  const CoRiskyProgressSection = () => {
    return (
      <Card>
        <div>
          <Text fontWeight={600}>CO เสี่ยงใช้ไม่หมด</Text>
          <br />
          <Text level={6} color='Text3'>
            ผ่านมาแล้วเกิน 50% ของระยะเวลา แต่ใช้ส่วนลดไม่ถึง 30% ของวงเงิน
          </Text>
        </div>
        <br />
        {data?.atRiskCos?.data && data?.atRiskCos?.data?.length > 0 ? (
          <>
            {data?.atRiskCos.data.map((item: any, index: number) => (
              <>
                <div style={{ maxHeight: 420, overflowY: "auto", paddingRight: 8 }}>
                  <Row justify={"space-between"} align='middle' gutter={8}>
                    <Col span={isViewCoList ? 23 : 24}>
                      <React.Fragment key={index}>
                        <div style={{ paddingBottom: 10 }}>
                          <Row justify={"space-between"} align='middle' gutter={8}>
                            <Col>
                              <Row justify={"start"} gutter={8}>
                                <Col>
                                  <Tag
                                    style={{
                                      backgroundColor: `${item?.creditMemoType?.color}20`,
                                      border: "none",
                                    }}
                                  >
                                    <Typography.Text
                                      style={{
                                        fontFamily: "IBM Plex Sans Thai",
                                        color: item?.creditMemoType?.color,
                                        fontWeight: "bold",
                                        fontSize: 15,
                                      }}
                                    >
                                      {item?.creditMemoType?.code}
                                    </Typography.Text>
                                  </Tag>
                                </Col>
                                <Col>
                                  <Text>{item?.name}</Text>
                                </Col>
                              </Row>
                            </Col>
                            <Col>
                              <Text color='EmeraldGreen' fontWeight={600}>
                                {numberFormatter(item.remaining)} ฿
                              </Text>
                              {" / "}
                              <Text color='Text3' level={6}>
                                {numberFormatter(item?.totalDiscount)} ฿
                              </Text>
                              <Text color='Grey' level={6} fontWeight={600}>
                                {" "}
                                ใช้ไป {numberFormatter(item?.used)} ฿
                              </Text>
                            </Col>
                          </Row>
                          <Row justify={"space-between"} align='middle' gutter={8}>
                            <Col span={22}>
                              <Progress
                                key={index}
                                strokeColor={item.creditMemoType?.color}
                                percent={item?.totalDiscount ? item?.remainingPercent : 0}
                                showInfo={false}
                              />
                            </Col>
                            <Col span={2}>
                              <Text level={6} fontWeight={500}>
                                เหลือ {numberFormatter(item?.remainingPercent)} %
                              </Text>
                            </Col>
                          </Row>
                        </div>
                      </React.Fragment>
                    </Col>
                    <Col>
                      <Permission permission={["coDiscountList", "view"]}>
                        <ActionBtn
                          onClick={() =>
                            navigate(
                              "/newDiscountCo/detailCODiscountList/" + item.coId + "/coDashboard",
                            )
                          }
                          icon={<UnorderedListOutlined />}
                        />
                      </Permission>
                    </Col>
                  </Row>
                  <Divider size='small' />
                </div>
              </>
            ))}
          </>
        ) : (
          <FlexCol style={{ height: "200px", alignItems: "center", justifyContent: "center" }}>
            <BarChartOutlined style={{ fontSize: 50, color: color.Text3 }} />
            <Text color='Text3'>ไม่มีข้อมูล</Text>
          </FlexCol>
        )}
      </Card>
    );
  };
  const exportMutiExcel = async () => {
    setIsExporting(true);
    const params = {
      company: profile.company,
      status: [searchStatus],
      startDate: period[0] && period[0].format("YYYY-MM-DD"),
      endDate: period[1] && period[1].format("YYYY-MM-DD"),
      zone: selectedZone.length > 0 ? selectedZone.join(",") : undefined,
    };
    let blob: Blob = new Blob([], {
      type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
    });
    try {
      const config = await excelMutiCoDashboard(params);
      if (config) {
        blob = new Blob([config], {
          type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
        });
      }
      startUploadProcess(
        "",
        progressValue,
        setProgressValue,
        {
          placement: "bottomRight",
          duration: 2,
          label: "กำลังเตรียมไฟล์ของคุณ รอสักครู่!",
          progressInterval: 900,
        },
        async () => {
          try {
            const a = document.createElement("a");
            a.href = URL.createObjectURL(blob);
            a.download = `co-dashboard-${new Date()
              .toLocaleDateString("us-EN", {
                year: "numeric",
                month: "2-digit",
                day: "2-digit",
              })
              .replace(/\//g, "-")}.xlsx`;
            document.body.appendChild(a);
            a.click();
            document.body.removeChild(a);
            window.URL.revokeObjectURL(a.href);
            // formatFileSize(blob.size);
          } catch (error) {
            message.error("Export Excel ไม่สำเร็จ");
          } finally {
            setIsExporting(false);
          }
        },
      );
    } catch (e: any) {
      message.error("Export Excel ไม่สำเร็จ");
      setIsExporting(false);
    }
  };
  return (
    <>
      <CardContainer>
        <PageTitle
          selectedTeam={selectedTeam}
          selectedZone={selectedZone}
          zoneFilter={zoneFilter}
          teamList={teamOptions}
          setSelectedTeam={setSelectedTeam}
          setSelectedZone={setSelectedZone}
          isExport={isExport}
          exportMutiExcel={exportMutiExcel}
        />
        <br />
        <SearchSection />
        <>
          {loading ? (
            <div
              style={{
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                height: "40vh",
              }}
            >
              <Spin size='large' />
            </div>
          ) : (
            <>
              <br />
              <DashboardSection />
              <br />
              <CoListProgressSection />
              <br />
              <Row justify={"space-between"} gutter={8}>
                <Col span={12}>
                  <CoZoneProgressSection />
                </Col>
                <Col span={12}>
                  <CoTypeProgressSection />
                </Col>
              </Row>
              {/* <br /> */}
              {/* <CoRiskyProgressSection /> */}
            </>
          )}
        </>
      </CardContainer>
      {isExporting && (
        <div
          style={{
            position: "fixed",
            inset: 0,
            backgroundColor: "rgba(128, 128, 128, 0.18)",
            cursor: "not-allowed",
            zIndex: 999,
          }}
        />
      )}
    </>
  );
};

const PageTitle = ({
  selectedTeam,
  selectedZone,
  zoneFilter,
  teamList,
  setSelectedTeam,
  setSelectedZone,
  isExport,
  exportMutiExcel,
}: any) => {
  return (
    <Row justify='space-between' align='middle' gutter={8}>
      <Col span={12}>
        <div>
          <span
            className='card-label font-weight-bolder text-dark'
            style={{
              fontSize: 22,
              fontWeight: "bold",
            }}
          >
            แดชบอร์ดส่วนลด CO
          </span>
        </div>
      </Col>

      <Col span={12}>
        <Row gutter={8} justify='end'>
          <Col span={10}>
            <Select
              value={selectedTeam}
              mode='multiple'
              maxTagCount='responsive'
              placeholder='ทุกทีม'
              data={teamList}
              onChange={(value) => {
                const zones = Array.from(
                  new Set(
                    value.flatMap((val) =>
                      teamList
                        .filter((team) => team.key.includes(val))
                        .flatMap((team) => team.zones)
                        .map((zone) => zone.zoneName),
                    ),
                  ),
                );
                setSelectedTeam(value);
                setSelectedZone(zones);
              }}
            />
          </Col>

          <Col span={10}>
            <Select
              value={selectedZone}
              mode='multiple'
              placeholder='ทุกเขต'
              maxTagCount='responsive'
              data={zoneFilter.map((zone: any) => ({
                label: zone.zoneName,
                value: zone.zoneName,
                key: zone.zoneName,
              }))}
              onChange={(value) => {
                if (value.length === 0) setSelectedTeam([]);
                const team = Array.from(
                  new Set(
                    value.flatMap((val) =>
                      teamList
                        .filter((zone) => zone.zones.some((zone) => zone.zoneName.includes(val)))
                        .map((team) => team.key),
                    ),
                  ),
                );
                setSelectedZone(value);
                setSelectedTeam(team);
              }}
            />
          </Col>

          {isExport && (
            <Col span={4}>
              <Button
                onClick={exportMutiExcel}
                type='primary'
                typeButton='primary'
                title={
                  <>
                    <VerticalAlignBottomOutlined style={{ fontSize: "18px" }} />
                    Export
                  </>
                }
                height={40}
              />
            </Col>
          )}
        </Row>
      </Col>
    </Row>
  );
};
